import apiClient from "./config";
import { COMMUNITY_ENDPOINTS } from "./endpoints";

/**
 * Community Service
 * Centralized API calls for community/posts features
 * Handles all post, comment, and interaction operations
 */
const communityService = {
  // ============= CATEGORIES =============

  /**
   * Get all post categories
   * @param {Object} [params] - Optional query params (e.g. { sort: "postCount" })
   * @returns {Promise} - Categories list
   */
  getAllCategories: (params = { sort: "postCount" }) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.GET_ALL_CATEGORIES, { params });
  },

  // ============= POSTS =============

  /**
   * Create a new post
   * @param {FormData} formData - Post data including content, category, images, is_post_anonymously
   * @returns {Promise} - Created post data
   */
  createPost: (formData) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.CREATE_POST, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  editPost: (formData) => {
    return apiClient.put(COMMUNITY_ENDPOINTS.EDIT_POST, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  /**
   * Get all posts with pagination, search, filter, and sort
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.length - Items per page
   * @param {string} [params.search] - Search term
   * @param {number} [params.category] - Category ID
   * @param {string} [params.sortBy] - Sort by: 'all', 'weekly', 'monthly', 'yearly'
   * @returns {Promise} - Posts list with pagination data
   */
  getAllPosts: (params) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.GET_ALL_POSTS, params);
  },

  /**
   * Get single post by ID
   * @param {number} postId - Post ID
   * @returns {Promise} - Post details
   */
  getPostById: (postId) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.GET_POST_BY_ID(postId));
  },

  /**
   * Delete a post
   * @param {number} postId - Post ID
   * @returns {Promise} - Deletion confirmation
   */
  deletePost: (postId) => {
    return apiClient.delete(COMMUNITY_ENDPOINTS.DELETE_POST(postId));
  },

  /**
   * Report a post
   * @param {number} postId - Post ID
   * @param {string} [reason] - Report reason
   * @returns {Promise} - Report confirmation
   */
  reportPost: (postId, reason) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.REPORT_POST(postId), { reason });
  },

  // ============= POST INTERACTIONS =============

  /**
   * Like or unlike a post (toggles)
   * @param {number} postId - Post ID
   * @returns {Promise} - Updated like count and status
   */
  likeUnlikePost: (postId) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.LIKE_UNLIKE_POST, { id: postId });
  },

  // ============= POST REACTIONS (EMOJI) =============

  /**
   * Add or update a reaction to a post
   * @param {number} postId - Post ID
   * @param {string} reactionType - Reaction type: 'like', 'love', 'haha', 'wow', 'sad', 'angry'
   * @returns {Promise} - Updated reaction counts and user reaction
   */
  addOrUpdateReaction: (postId, reactionType) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.ADD_REACTION, {
      post_id: postId,
      reaction_type: reactionType,
    });
  },

  /**
   * Remove user's reaction from a post
   * @param {number} postId - Post ID
   * @returns {Promise} - Updated reaction counts
   */
  removeReaction: (postId) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.REMOVE_REACTION, {
      post_id: postId,
    });
  },

  /**
   * Get all reactions for a post
   * @param {number} postId - Post ID
   * @returns {Promise} - Reaction counts by type and user's reaction
   */
  getPostReactions: (postId) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.GET_REACTIONS, {
      post_id: postId,
    });
  },

  // ============= COMMENTS =============

  /**
   * Add a comment or reply to a post
   * @param {FormData} formData - Comment data including post_id, content, image_path, parent_id
   * @returns {Promise} - Created comment data
   */
  addComment: (formData) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.ADD_COMMENT, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  /**
   * Edit a comment (within 5 minutes)
   * @param {FormData} formData - Comment data including id, content, image_path
   * @returns {Promise} - Updated comment data
   */
  editComment: (formData) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.EDIT_COMMENT, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  /**
   * Get all comments for a post with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.post_id - Post ID
   * @param {number} params.page - Page number
   * @param {number} params.length - Items per page
   * @param {number} [params.comment_id] - Optional comment ID to highlight
   * @returns {Promise} - Comments list with nested replies
   */
  getPostComments: (params) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.GET_POST_COMMENTS, params);
  },
  getPreviewLink: (params) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.CREATE_PREVIEW, {
      params, // ✅ this is required
    });
  },

  /**
   * Delete a comment (cascades to replies)
   * @param {number} commentId - Comment ID
   * @returns {Promise} - Deletion confirmation
   */
  deleteComment: (commentId) => {
    return apiClient.delete(COMMUNITY_ENDPOINTS.DELETE_COMMENT(commentId));
  },

  /**
   * Like or unlike a comment (toggles)
   * @param {number} commentId - Comment ID
   * @returns {Promise} - Updated like count and status
   */
  likeUnlikeComment: (commentId) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.LIKE_UNLIKE_COMMENT, {
      id: commentId,
    });
  },

  // ============= COMMENT REACTIONS (EMOJI) =============

  /**
   * Add or update a reaction to a comment
   * @param {number} commentId - Comment ID
   * @param {string} reactionType - Reaction type: 'like', 'love', 'haha', 'wow', 'sad', 'angry'
   * @returns {Promise} - Updated reaction counts and user reaction
   */
  addOrUpdateCommentReaction: (commentId, reactionType) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.ADD_COMMENT_REACTION, {
      comment_id: commentId,
      reaction_type: reactionType,
    });
  },

  /**
   * Remove user's reaction from a comment
   * @param {number} commentId - Comment ID
   * @returns {Promise} - Updated reaction counts
   */
  removeCommentReaction: (commentId) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.REMOVE_COMMENT_REACTION, {
      comment_id: commentId,
    });
  },

  /**
   * Get all reactions for a comment
   * @param {number} commentId - Comment ID
   * @returns {Promise} - Reaction counts by type and user's reaction
   */
  getCommentReactions: (commentId) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.GET_COMMENT_REACTIONS, {
      comment_id: commentId,
    });
  },

  // ============= TRENDING & HOT TOPICS =============

  /**
   * Get trending posts (top 5 most liked posts)
   * @param {Object} params - Query parameters (not used, kept for backward compatibility)
   * @returns {Promise} - Top 5 trending posts ordered by likes_count DESC
   */
  getTrendingPosts: (params = {}) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.TRENDING_POSTS);
  },

  /**
   * Get hot topics (top 5 most commented posts)
   * @param {Object} params - Query parameters (not used, kept for backward compatibility)
   * @returns {Promise} - Top 5 hot topics ordered by comments_count DESC
   */
  getHotTopics: (params = {}) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.HOT_TOPICS);
  },

  /**
   * Get popular topics (top 3 categories with most posts)
   * @returns {Promise} - Top 3 popular categories ordered by post_count DESC
   */
  getPopularTopics: () => {
    return apiClient.get(COMMUNITY_ENDPOINTS.POPULAR_TOPICS);
  },
  searchUsers: (query, limit = 5) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.SEARCH_USERS, {
      params: { q: query, limit },
    });
  },
  rePost: (params) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.REPOST, params);
  },
  follow: (id) => {
    return apiClient.post(`${COMMUNITY_ENDPOINTS.FOLLOW}/${id}`, { user_id: id });
  },

  /**
   * Unfollow a user
   * @param {number} userId - User ID to unfollow
   * @returns {Promise} - Unfollow response
   */
  unfollow: (userId) => {
    return apiClient.delete(COMMUNITY_ENDPOINTS.UNFOLLOW(userId));
  },

  /**
   * Get list of users that a user is following
   * @param {number} userId - User ID
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 10)
   * @returns {Promise} - Following users list with pagination
   */
  getFollowing: (userId, page = 1, limit = 10) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.GET_FOLLOWING(userId), {
      params: { page, limit }
    });
  },

  /**
   * Get list of followers for a user
   * @param {number} userId - User ID
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 10)
   * @returns {Promise} - Followers list with pagination
   */
  getFollowers: (userId, page = 1, limit = 10) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.GET_FOLLOWERS(userId), {
      params: { page, limit }
    });
  },

  /**
   * Get follow statistics for a user (followers and following counts)
   * @param {number} userId - User ID
   * @returns {Promise} - Follow statistics
   */
  getFollowStats: (userId) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.GET_FOLLOW_STATS(userId));
  },

  /**
   * Get follow status for a specific user (check if current user is following them)
   * @param {number} userId - User ID to check
   * @returns {Promise} - Follow status (is_following: boolean)
   */
  getFollowStatus: (userId) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.GET_FOLLOW_STATUS(userId));
  },

  /**
   * Get quick user information for profile preview
   * @param {number} userId - User ID
   * @returns {Promise} - User quick info (name, photo, role, experience)
   */
  getUserQuickInfo: (userId) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.GET_USER_QUICK_INFO(userId));
  },

  /**
   * Toggle save (bookmark) on a post — adds if not saved, removes if saved
   * @param {number} postId - Post ID
   * @returns {Promise} - Toggle response
   */
  togglePostSave: (postId) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.TOGGLE_POST_SAVE, {
      post_id: postId,
    });
  },

  /**
   * Get all saved posts for the authenticated user
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 10)
   * @returns {Promise} - Saved posts list with pagination
   */
  getSavedPosts: (page = 1, limit = 10) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.GET_SAVED_POSTS, {
      params: { page, limit },
    });
  },

  /**
   * Check whether a specific post is saved by the current user
   * @param {number} postId - Post ID
   * @returns {Promise} - { success, data: { is_saved: 0 | 1 } } (or similar)
   */
  checkPostSaved: (postId) => {
    return apiClient.get(COMMUNITY_ENDPOINTS.CHECK_POST_SAVED, {
      params: { post_id: postId },
    });
  },

  /**
   * Submit a wishlist role name from the community "Not seeing your
   * profession?" input. One row per user — repeat submits return
   * already_submitted=true without creating a new row.
   */
  submitRequestedRole: (roleName) => {
    return apiClient.post(COMMUNITY_ENDPOINTS.SUBMIT_REQUESTED_ROLE, {
      role_name: roleName,
    });
  },

  getActivePoll: () => {
    return apiClient.get(COMMUNITY_ENDPOINTS.GET_ACTIVE_POLL);
  },

  submitPollVote: (poll_id, option_ids) => {
    const isMulti = Array.isArray(option_ids);
    return apiClient.post(COMMUNITY_ENDPOINTS.SUBMIT_POLL_VOTE, {
      poll_id,
      ...(isMulti ? { option_ids } : { option_id: option_ids }),
    });
  },

  // ============= WEEKLY ALLOWANCE (FREE TIER) =============

  /**
   * Get the current user's weekly community allowance.
   * @returns {Promise} - { post: {used,limit,remaining,limit_reached,...},
   *                        comment: {...} }. limit = -1 means unlimited.
   */
  getCommunityUsage: () => {
    return apiClient.get(COMMUNITY_ENDPOINTS.COMMUNITY_USAGE);
  },
};

export default communityService;
